import React, { useEffect } from 'react';
import { ProductTableRow } from '../ProductTableRow';

import './ProductsTable.scss';
import { BasketProduct, Product, productsActions as _productsActions } from '../../../store/products';
import { Button, EButtonType } from '@repo/apg-ui';
import { ProductFilter } from '../../../store/products/types';
import { createSubProductUrn, getBasketEntryId } from '../../../utils/products';
import moment from 'moment';
import { useIntl } from 'react-intl';

interface Props {
  products: Product[];
  productsByUrn: IDictionary<Product>;
  possibleBookingDates: any;
  productFiltersDataByUrn: IDictionary<any>;
  productFiltersByUrn: IDictionary<ProductFilter>;
  setProductFilterData: any;
  getAvailabilityForDigitalProduct: any;
  isLoading: any;
  addToBasket: (product: Product, productUrn: string) => void;
  basketProductsByUrn: IDictionary<BasketProduct>;
  basketProducts: BasketProduct[];
  setLoading: (key: string, value: boolean) => void;
  isCalendarLoading: boolean;
  isBatchSelected: (product: Product, string) => boolean;
  batchSelectOrDeselectProduct: (product: Product) => void;
  setBottomBarFilter: (boolean) => void;
  removeFromBasket: any;
  searchParameters: any;
  productsActions: typeof _productsActions;
  updateBasketProductData: (productUrn, payload, basketEntryId) => void;
}

export const ProductTable = ({
  products,
  possibleBookingDates,
  setProductFilterData,
  productFiltersDataByUrn,
  productFiltersByUrn,
  productsByUrn,
  getAvailabilityForDigitalProduct,
  isLoading,
  addToBasket,
  basketProducts,
  basketProductsByUrn,
  setLoading,
  isCalendarLoading,
  removeFromBasket,
  isBatchSelected,
  batchSelectOrDeselectProduct,
  setBottomBarFilter,
  searchParameters,
  productsActions,
  updateBasketProductData,
}: Props) => {
  const [shownProducts, setShownProducts] = React.useState<Product[]>([]);
  const [currentlyShownCounter, setCurrentlyShownCounter] = React.useState(0);
  const intl = useIntl();

  useEffect(() => {
    if (products) {
      setShownProducts(products.slice(0, 10));
      setCurrentlyShownCounter(10);
    }
  }, [products]);

  useEffect(() => {
    if (products.length && basketProducts.length) {
      products.forEach((product) => {
        // only showing subrows when selected timeslot of above filter is "1 = all day"
        // other timeslot will be filtered automatically
        if (
          product &&
          searchParameters?.timeslot === 1 &&
          product?.timeslot === 1 &&
          !productFiltersByUrn[product?.producturn]
        ) {
          showSubrowsIfExistInBasket(product);
        }
      });
    }
  }, [products, basketProducts]);

  const showSubrowsIfExistInBasket = (product) => {
    const filteredProducts = basketProducts?.filter((basketProduct) => {
      const productStartDate = moment(product.startdate).format('DD/MM/YYYY');
      const productEndDate = moment(product.enddate).format('DD/MM/YYYY');
      const basketStartDate = moment(basketProduct.basketstartdate).format('DD/MM/YYYY');
      const basketEndDate = moment(basketProduct.basketenddate).format('DD/MM/YYYY');
      return (
        basketProduct.producturn === product.producturn &&
        basketStartDate === productStartDate &&
        basketEndDate === productEndDate &&
        basketProduct.baskettimeslot !== searchParameters?.timeslot
      );
    });

    if (filteredProducts?.length > 0) {
      const allTimeslots = filteredProducts.map((product) => product.baskettimeslot).sort((a, b) => a - b);

      if (allTimeslots.length) {
        const productFilterData = productFiltersByUrn[product.producturn] || null;
        const newTimeslots = [
          searchParameters?.timeslot || [],
          ...(productFilterData?.timeslots || []),
          ...allTimeslots,
        ].filter((value, index, self) => self.indexOf(value) === index);

        productsActions.setProductFilterData(product.producturn, {
          timeslots: newTimeslots,
        });

        newTimeslots.forEach((timeslot) => {
          if (timeslot != product.timeslot) {
            const subproducturn = createSubProductUrn(product.producturn, timeslot);
            setProductFilterData(subproducturn, {
              producturn: product.producturn,
              timeslots: [timeslot],
              isBudgetChanged: false,
              isNewTimeslot: false,
            });
          }
        });
      }
    }
  };

  const showMore = () => {
    const newShownProducts = [...shownProducts];
    const newCurrentlyShownCounter = products.length;
    setShownProducts([...newShownProducts, ...products.slice(currentlyShownCounter, newCurrentlyShownCounter)]);
    setCurrentlyShownCounter(newCurrentlyShownCounter);
  };

  const getProductFilterDataWithUrn = (producturn: string, timeslot) => {
    const productUrn = createSubProductUrn(producturn, timeslot);
    const productFilterData = productFiltersByUrn[productUrn] || null;
    const initialProduct = productsByUrn[producturn] || null;
    const subProductConfig = productFiltersDataByUrn[productUrn] || null;
    const subRowProduct = { ...initialProduct, ...subProductConfig };
    const basketProduct = getBasketEntryId(
      producturn,
      subRowProduct,
      productFilterData,
      basketProducts,
      basketProductsByUrn,
      timeslot,
    );
    return { productUrn, productFilterData, subRowProduct, basketProduct };
  };

  const renderSubrows = (product, timeslot, index) => {
    const { productUrn, productFilterData, subRowProduct, basketProduct } = getProductFilterDataWithUrn(
      product.producturn,
      timeslot,
    );

    return (
      <div className='additional-period' key={index}>
        <ProductTableRow
          productUrn={productUrn}
          productsActions={productsActions}
          updateBasketProductData={updateBasketProductData}
          isAdditionalPeriod={true}
          product={subRowProduct}
          productFilterData={productFilterData}
          possibleBookingDates={possibleBookingDates}
          setProductFilterData={setProductFilterData}
          productFiltersByUrn={productFiltersByUrn}
          productFiltersDataByUrn={productFiltersDataByUrn}
          getAvailabilityForDigitalProduct={getAvailabilityForDigitalProduct}
          productsByUrn={productsByUrn}
          isLoading={isLoading}
          addToBasket={addToBasket}
          setLoading={setLoading}
          isCalendarLoading={isCalendarLoading}
          removeFromBasket={removeFromBasket}
          isInBasket={!!basketProduct}
          basketEntryId={basketProduct?.basketentryid}
          basketProduct={basketProduct}
          isBatchSelected={isBatchSelected}
          batchSelectOrDeselectProduct={batchSelectOrDeselectProduct}
          setBottomBarFilter={setBottomBarFilter}
          searchParameters={searchParameters}
        />
      </div>
    );
  };

  return (
    <>
      {products && products.length > 0 && (
        <div className='product-table'>
          {shownProducts.map((product: Product, index) => {
            if (!product) return null;
            const productFilterData = productFiltersByUrn[product.producturn] || null;
            const firstTimeslot =
              productFilterData?.timeslots?.length > 0 ? productFilterData?.timeslots[0] : searchParameters?.timeslot;

            const initialProductSubUrn = createSubProductUrn(product?.producturn, firstTimeslot);
            const initialProduct = productsByUrn[product?.producturn] || null;
            const productFilterConfig = productFiltersDataByUrn?.[product?.producturn] || null;
            const subProductFilterConfig = productFiltersDataByUrn?.[initialProductSubUrn] || null;
            const fullProduct = {
              ...initialProduct,
              ...productFilterConfig,
              ...subProductFilterConfig,
            };

            const basketProduct = getBasketEntryId(
              product.producturn,
              fullProduct,
              productFilterData,
              basketProducts,
              basketProductsByUrn,
              firstTimeslot,
            );

            return (
              <div>
                <ProductTableRow
                  productUrn={product.producturn}
                  productsActions={productsActions}
                  updateBasketProductData={updateBasketProductData}
                  product={fullProduct}
                  productFilterData={productFiltersByUrn[product.producturn]}
                  possibleBookingDates={possibleBookingDates}
                  setProductFilterData={setProductFilterData}
                  productFiltersByUrn={productFiltersByUrn}
                  productFiltersDataByUrn={productFiltersDataByUrn}
                  getAvailabilityForDigitalProduct={getAvailabilityForDigitalProduct}
                  productsByUrn={productsByUrn}
                  isLoading={isLoading}
                  addToBasket={addToBasket}
                  setLoading={setLoading}
                  isCalendarLoading={isCalendarLoading}
                  removeFromBasket={removeFromBasket}
                  isInBasket={!!basketProduct}
                  basketEntryId={basketProduct?.basketentryid}
                  basketProduct={basketProduct}
                  isBatchSelected={isBatchSelected}
                  batchSelectOrDeselectProduct={batchSelectOrDeselectProduct}
                  setBottomBarFilter={setBottomBarFilter}
                  searchParameters={searchParameters}
                />

                {/*
                                TODO:: Multiple range is remove for now, but do not remove this code, it will be used in the future
                                {productFilterData &&
                                    productFilterData.dateRanges &&
                                    productFilterData.dateRanges.length > 1 &&
                                    productFilterData.dateRanges.map(
                                        (dateRange, index) => {
                                            if (index == 0) {
                                                return;
                                            }
                                            return (
                                                <div
                                                    className="additional-period"
                                                    key={index}
                                                >
                                                    <ProductTableRow

                                                        isAdditionalPeriod={
                                                            true
                                                        }
                                                        product={
                                                            productsByUrn[
                                                                product
                                                                    .producturn
                                                            ]
                                                        }
                                                        productFilterData={
                                                            productFilterData
                                                        }
                                                        possibleBookingDates={
                                                            possibleBookingDates
                                                        }
                                                        setProductFilterData={
                                                            setProductFilterData
                                                        }
                                                        productFiltersByUrn={
                                                            productFiltersByUrn
                                                        }
                                                        getAvailabilityForDigitalProduct={
                                                            getAvailabilityForDigitalProduct
                                                        }
                                                        productsByUrn={
                                                            productsByUrn
                                                        }
                                                        isLoading={isLoading}
                                                        addToBasket={
                                                            addToBasket
                                                        }
                                                        isInBasket={
                                                            basketProductsByUrn
                                                                ? !!basketProductsByUrn[
                                                                      product
                                                                          .producturn
                                                                  ]
                                                                : false
                                                        }
                                                        setLoading={setLoading}
                                                        isCalendarLoading={
                                                            isCalendarLoading
                                                        }
                                                        dateRangeIndex={index}
                                                        removeFromBasket={
                                                            removeFromBasket
                                                        }
                                                        basketEntries={
                                                            basketProductsByUrn
                                                        }
                                                        isBatchSelected={
                                                            isBatchSelected
                                                        }
                                                        batchSelectOrDeselectProduct={
                                                            batchSelectOrDeselectProduct
                                                        }
                                                        setBottomBarFilter={
                                                            setBottomBarFilter
                                                        }
                                                        searchParameters={
                                                            searchParameters
                                                        }
                                                    />
                                                </div>
                                            );
                                        },
                                    )}*/}

                {productFilterData &&
                  productFilterData.timeslots &&
                  productFilterData.timeslots.length > 1 &&
                  productFilterData.timeslots.map((timeslot, index) => {
                    if (index == 0) {
                      return;
                    }

                    return renderSubrows(product, timeslot, index);
                  })}
              </div>
            );
          })}

          {products.length > currentlyShownCounter && products.length !== 0 && (
            <div className='d-flex position-relative justify-content-center align-items-center py-5 table-row-top'>
              <Button buttonType={EButtonType.InvertedPillMedium} onClick={showMore}>
                Show more
              </Button>
            </div>
          )}
        </div>
      )}
      {products && products.length === 0 && (
        <div className='product-table'>
          <div className='d-flex justify-content-center align-items-center py-5 table-row-top'>
            <div className='empty-table-text' style={{ color: 'red', fontSize: '14px' }}>
              {intl.formatMessage({
                id: 'ec.modals.resultList.PanelLoading',
                defaultMessage: 'Netze werde geladen und in wenigen Sekunden automatisch angezeigt.',
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
